/*
Control Categories Service

API client for CTR_categories CRUD operations.
Backend: /api/v1/ctr/categories
*/

import apiClient from './apiService';
import { AxiosResponse } from 'axios';
import type { AuditRecord } from '../components/common/AuditHistoryModal';

// Use shared apiClient
const apiService = apiClient;

// Types
export interface ControlCategory {
  id: number;
  entity_id: number;
  category_code: string;
  category_name: string;
  description?: string;
  code_seq?: number;
  code?: string;
  is_active: number;
  is_approved: number;
  created_by?: number;
  created_on?: string;
  modified_by?: number;
  modified_on?: string;
  // Approval workflow (maker-checker)
  workflow_state?: 'draft' | 'submitted' | 'approved' | 'rejected';
  submitted_by?: number;
  submitted_on?: string;
  approved_by?: number;
  approved_on?: string;
  approval_note?: string;
  rejected_by?: number;
  rejected_on?: string;
  rejection_reason?: string;
}

export interface CategoryQueryParams {
  status?: 'ALL' | 'ACTIVE' | 'INACTIVE';
  search?: string;
  page?: number;
  limit?: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  meta?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

class ControlCategoriesService {
  private basePath = '/api/v1/ctr/categories';

  async getAll(params?: CategoryQueryParams): Promise<ControlCategory[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.status && params.status !== 'ALL') {
        queryParams.append('status', params.status);
      }
      if (params?.search) {
        queryParams.append('search', params.search);
      }
      if (params?.page) {
        queryParams.append('page', params.page.toString());
      }
      if (params?.limit) {
        queryParams.append('limit', params.limit.toString());
      }

      const qs = queryParams.toString();
      const url = `${this.basePath}${qs ? `?${qs}` : ''}`;
      const response: AxiosResponse<ApiResponse<ControlCategory[]>> = await apiService.get(url);

      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.error || 'Failed to fetch control categories');
    } catch (error: any) {
      console.error('Error fetching control categories:', error);
      if (error.response?.status === 401) window.location.href = '/login';
      throw error;
    }
  }

  async getById(id: number): Promise<ControlCategory> {
    try {
      const response: AxiosResponse<ApiResponse<ControlCategory>> =
        await apiService.get(`${this.basePath}/${id}`);
      if (response.data.success && response.data.data) return response.data.data;
      throw new Error(response.data.error || 'Not found');
    } catch (error: any) {
      console.error(`Error fetching control category ${id}:`, error);
      if (error.response?.status === 401) window.location.href = '/login';
      throw error;
    }
  }

  async create(data: Partial<ControlCategory>): Promise<ControlCategory> {
    try {
      const response: AxiosResponse<ApiResponse<ControlCategory>> =
        await apiService.post(this.basePath, data);
      if (response.data.success && response.data.data) return response.data.data;
      throw new Error(response.data.error || 'Failed to create');
    } catch (error: any) {
      console.error('Error creating control category:', error);
      if (error.response?.status === 401) window.location.href = '/login';
      throw error;
    }
  }

  async update(id: number, data: Partial<ControlCategory>): Promise<ControlCategory> {
    try {
      const response: AxiosResponse<ApiResponse<ControlCategory>> =
        await apiService.put(`${this.basePath}/${id}`, data);
      if (response.data.success && response.data.data) return response.data.data;
      throw new Error(response.data.error || 'Failed to update');
    } catch (error: any) {
      console.error(`Error updating control category ${id}:`, error);
      if (error.response?.status === 401) window.location.href = '/login';
      throw error;
    }
  }

  async delete(id: number): Promise<void> {
    try {
      const response: AxiosResponse<ApiResponse<void>> =
        await apiService.delete(`${this.basePath}/${id}`);
      if (!response.data.success) throw new Error(response.data.error || 'Failed to delete');
    } catch (error: any) {
      console.error(`Error deleting control category ${id}:`, error);
      if (error.response?.status === 401) window.location.href = '/login';
      throw error;
    }
  }

  async toggleStatus(id: number): Promise<ControlCategory> {
    try {
      const response: AxiosResponse<ApiResponse<ControlCategory>> =
        await apiService.patch(`${this.basePath}/${id}/toggle`);
      if (response.data.success && response.data.data) return response.data.data;
      throw new Error(response.data.error || 'Failed to toggle status');
    } catch (error: any) {
      console.error(`Error toggling control category ${id} status:`, error);
      if (error.response?.status === 401) window.location.href = '/login';
      throw error;
    }
  }

  /** Submit a draft/rejected record for approval. */
  async submit(id: number): Promise<void> {
    const res = await apiService.post<ApiResponse<void>>(`${this.basePath}/${id}/submit`);
    if (!res.data.success) throw new Error(res.data.error || 'Submit failed');
  }

  /** Approve a submitted record (approver scope only). */
  async approve(id: number, note?: string): Promise<void> {
    const res = await apiService.post<ApiResponse<void>>(`${this.basePath}/${id}/approve`, { note });
    if (!res.data.success) throw new Error(res.data.error || 'Approve failed');
  }

  /** Reject a submitted record with mandatory reason (approver scope only). */
  async reject(id: number, reason: string): Promise<void> {
    const res = await apiService.post<ApiResponse<void>>(`${this.basePath}/${id}/reject`, { reason });
    if (!res.data.success) throw new Error(res.data.error || 'Reject failed');
  }

  /** Reopen an approved/rejected record back to draft (approver scope only). */
  async reopen(id: number): Promise<void> {
    const res = await apiService.post<ApiResponse<void>>(`${this.basePath}/${id}/reopen`);
    if (!res.data.success) throw new Error(res.data.error || 'Reopen failed');
  }

  /** Fetch audit/workflow history for a category. */
  async getHistory(id: number): Promise<AuditRecord[]> {
    try {
      const response = await apiService.get(`${this.basePath}/${id}/history`);
      if (response.data.success && response.data.data) {
        return (response.data.data as any[]).map((r: any) => ({
          audit_id: r.id,
          action: r.event_type === 'state_transition' ? 'STATE' : 'EDIT',
          changed_by_username: r.changed_by_username || 'Unknown',
          changed_on: r.changed_on,
          change_reason: r.remarks,
        }));
      }
      throw new Error(response.data.error || 'Failed to fetch history');
    } catch (error: any) {
      if (error.response?.status === 401) window.location.href = '/login';
      throw error;
    }
  }
}

const controlCategoriesService = new ControlCategoriesService();
export default controlCategoriesService;
